import { useCallback, useEffect, useState } from 'react';
import Dropdown from '../components/Dropdown';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import SearchableSelect from '../components/SearchableSelect';
import StatusBadge from '../components/StatusBadge';
import { ResetPasswordModal, UserEditModal } from '../components/UserAccountModals';
import TeacherFields, {
  EMPTY_TEACHER_PICKS,
  EMPTY_TEACHER_VALUES,
  cityGap,
  teacherPayload,
  teacherValidators,
} from '../components/TeacherFields';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import useForm from '../hooks/useForm';
import * as v from '../validation/rules';
import { REGISTRATION_CODES, UPLOAD_CODES } from '../validation/serverCodes';
import { api } from '../api/client';

/**
 * Teachers and students, on their own screen.
 *
 * <p>They used to share the Internal Users list, which never worked: internal accounts
 * are created here by an admin and number in the dozens, while these arrive by public
 * sign-up and number in the thousands. One list meant the screen for creating a Support
 * Executive was also the screen for finding a student, with columns that suited neither.
 *
 * <p>The split is enforced by the API's `group` parameter rather than by the role filter
 * alone, so this screen cannot be made to show a staff account however it is filtered.
 *
 * <p>Teachers are also created here, which is why the button is on this screen rather
 * than on Internal Users: a teacher belongs with the teachers, not with the Support
 * Executives. The form is the registration form — the same component, so a field added
 * to one is a field added to both — and it opens the account in exactly the state a
 * self-registration does.
 */

const ROLES = [
  { value: '', label: 'Teachers and students' },
  { value: 'TEACHER', label: 'Teachers' },
  { value: 'STUDENT', label: 'Students' },
];

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

export default function RegisteredUsersPage() {
  const [page, setPage] = useState({ items: [], page: 0, totalItems: 0, hasNext: false });
  const [filters, setFilters] = useState({ role: '', status: '', query: '' });
  // The whole { id, name }, so the box can print the chosen state without a second
  // lookup to turn the id back into a label.
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [picks, setPicks] = useState(EMPTY_TEACHER_PICKS);
  const [cityError, setCityError] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  // The account being corrected, and the one being given a new password. Teachers and
  // students could be suspended and nothing else; a wrong number on a self-registered
  // account had nobody who could fix it, the holder included.
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);

  const searchStates = useCallback((q) => api.lookupStates(q), []);

  const createForm = useForm({
    initialValues: EMPTY_TEACHER_VALUES,
    // Neither is the creator's to supply: an administrator should not be inventing
    // somebody else's password, and they do not hold the teacher's ID document.
    validators: teacherValidators({ requirePassword: false, requireIdProof: false }),
    crossField: v.passwordsMatch,
  });

  const load = useCallback(
    async (pageNumber = 0) => {
      setLoading(true);
      try {
        setPage(
          await api.adminListUsers({
            group: 'REGISTERED',
            role: filters.role || undefined,
            status: filters.status || undefined,
            stateId: state?.id ?? undefined,
            query: filters.query,
            page: pageNumber,
            size: 20,
          }),
        );
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [filters.role, filters.status, filters.query, state],
  );

  useEffect(() => {
    // Debounced so typing in the search box fires one request, not one per keystroke.
    const handle = setTimeout(() => load(0), 250);
    return () => clearTimeout(handle);
  }, [load]);

  const createTeacher = createForm.handleSubmit(async (values) => {
    setCreateError(null);
    const gap = cityGap(picks);
    setCityError(gap);
    if (gap) return;
    try {
      const created = await api.adminCreateTeacher(teacherPayload(values, picks), values.idProof);
      setShowCreate(false);
      createForm.reset(EMPTY_TEACHER_VALUES);
      setPicks(EMPTY_TEACHER_PICKS);
      setGeneratedPassword(created.generatedPassword ?? null);
      setToast(`${created.user.fullName} created — invite sent`);
      await load(0);
    } catch (err) {
      if (err?.code === 'invalid_city') {
        setCityError(err.message);
        return;
      }
      if (!createForm.applyServerErrors(err, { ...REGISTRATION_CODES, ...UPLOAD_CODES })) {
        setCreateError(err);
      }
    }
  });

  async function toggleStatus(row) {
    const next = row.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
    try {
      await api.adminUpdateStatus(row.id, next);
      setToast(`${row.fullName} is now ${next.toLowerCase()}`);
      await load(page.page);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Registered Users</h1>
          <p>
            Teachers and students who signed up themselves. Accounts are not created here —
            a teacher's verification is worked from Teacher Verification. The state filter
            reads the address on the account, which is what the State column shows.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Icon name="plus" size={15} />
          New teacher
        </button>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      {generatedPassword && (
        <InfoBanner>
          Temporary password: <strong className="mono">{generatedPassword}</strong> — shown once, and
          only because this environment enables it. They must change it at their next sign-in.
        </InfoBanner>
      )}

      <section className="card card-pad">
        <div className="toolbar">
          <div style={{ width: 200 }}>
            <Dropdown
              id="roleFilter"
              options={ROLES}
              value={filters.role}
              onChange={(role) => setFilters({ ...filters, role })}
              ariaLabel="Filter by role"
            />
          </div>
          <div style={{ width: 170 }}>
            <Dropdown
              id="statusFilter"
              options={STATUSES}
              value={filters.status}
              onChange={(status) => setFilters({ ...filters, status })}
              ariaLabel="Filter by status"
            />
          </div>
          <div style={{ width: 220 }}>
            <SearchableSelect
              id="stateFilter"
              value={state}
              onChange={setState}
              loadOptions={searchStates}
              placeholder="All states"
              ariaLabel="Filter by state"
            />
          </div>
          <input
            className="input"
            style={{ flex: '1 1 220px' }}
            placeholder="Search name, mobile or email"
            aria-label="Search registered users"
            maxLength={100}
            value={filters.query}
            onChange={(event) => setFilters({ ...filters, query: event.target.value })}
          />
        </div>
      </section>

      <section className="card">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Mobile</th>
                <th>School</th>
                <th>State</th>
                <th>Status</th>
                <th>Verification</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {page.items.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="cell-user">
                      <div>
                        {row.fullName}
                        <span>{row.email ?? '—'}</span>
                      </div>
                    </div>
                  </td>
                  <td>{row.roleLabel}</td>
                  <td className="mono">{row.mobile}</td>
                  <td>
                    {/* The typed name is shown when there is no master row yet, because
                        an em dash would read as "no school" when one was in fact given. */}
                    {schoolOf(row) ?? <span className="muted">—</span>}
                  </td>
                  <td>{row.state ?? <span className="muted">—</span>}</td>
                  <td>
                    <StatusBadge status={row.status} />
                  </td>
                  <td>
                    {row.teacher ? <StatusBadge status={row.teacher.verificationStatus} /> : '—'}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn btn-ghost btn-sm"
                            onClick={() => setEditing(row)}>
                      Edit
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm"
                            onClick={() => setResetting(row)}>
                      Reset password
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${row.status === 'SUSPENDED' ? 'btn-secondary' : 'btn-danger'}`}
                      onClick={() => toggleStatus(row)}
                    >
                      {row.status === 'SUSPENDED' ? 'Reactivate' : 'Suspend'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div className="empty">Loading…</div>}
        {!loading && page.items.length === 0 && (
          <div className="empty">No registered users match these filters.</div>
        )}

        <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}>
          <span className="muted small">{page.totalItems.toLocaleString()} account(s)</span>
          <span style={{ display: 'flex', gap: 'var(--s0)' }}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={page.page === 0}
                    onClick={() => load(page.page - 1)}>
              Previous
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!page.hasNext}
                    onClick={() => load(page.page + 1)}>
              Next
            </button>
          </span>
        </div>
      </section>

      {showCreate && (
        <Modal
          title="New teacher"
          subtitle="The registration form, filled in on their behalf"
          width={760}
          onClose={() => setShowCreate(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button type="submit" form="create-teacher" className="btn btn-primary"
                      disabled={createForm.submitting}>
                <Icon name="check" size={15} />
                {createForm.submitting ? 'Creating…' : 'Create teacher'}
              </button>
            </>
          }
        >
          <form id="create-teacher" {...createForm.formProps} onSubmit={createTeacher} className="stack">
            <ErrorBanner error={createError} onDismiss={() => setCreateError(null)} />
            <InfoBanner>
              The account opens unverified, exactly as a self-registration does: they sign in, upload
              their school ID if you have not, and a Sales Representative verifies them. Creating the
              account is not the same as vouching for it.
            </InfoBanner>
            <TeacherFields
              form={createForm}
              picks={picks}
              onPicks={setPicks}
              cityError={cityError}
              requirePassword={false}
              requireIdProof={false}
              passwordHint="Leave blank to generate one and email it; they change it at first sign-in"
            />
          </form>
        </Modal>
      )}

      {editing && (
        <UserEditModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={async (saved) => {
            setEditing(null);
            await load(page.page);
            setToast(`${saved.fullName} updated`);
          }}
        />
      )}

      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={async (result) => {
            setResetting(null);
            setGeneratedPassword(result.generatedPassword ?? null);
            await load(page.page);
            setToast(`${result.user.fullName} must choose a new password at next sign-in`);
          }}
        />
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2600)} />
    </>
  );
}

/** The school on either profile block, master row or typed name, whichever exists. */
function schoolOf(row) {
  const profile = row.teacher ?? row.student;
  if (!profile) return null;
  if (profile.school) return profile.school.name;
  if (!profile.schoolNameRaw) return null;
  // Said out loud rather than shown as a plain name: an unlinked school is why a
  // teacher's activation codes have nowhere to go.
  return (
    <>
      {profile.schoolNameRaw}
      <span className="muted small"> · not linked</span>
    </>
  );
}
